export type ErrorMonitoringProvider = "sentry" | "datadog" | "none";

const PROVIDER_VALUES: ErrorMonitoringProvider[] = ["sentry", "datadog", "none"];

const normalizeProvider = (value?: string | null): ErrorMonitoringProvider | null => {
  const normalized = value?.trim().toLowerCase();
  if (!normalized) return null;
  if (PROVIDER_VALUES.includes(normalized as ErrorMonitoringProvider)) {
    return normalized as ErrorMonitoringProvider;
  }
  return null;
};

/**
 * Static process.env reads so Next/webpack inline NEXT_PUBLIC_* into client bundles.
 * Dynamic process.env[key] is not replaced and stays undefined in the browser.
 */
const getExplicitProvider = (): ErrorMonitoringProvider | null =>
  normalizeProvider(
    process.env.NEXT_PUBLIC_ERROR_MONITORING_PROVIDER ||
      process.env.REACT_APP_ERROR_MONITORING_PROVIDER ||
      process.env.ERROR_MONITORING_PROVIDER,
  );

const hasSentryCredentials = (): boolean =>
  Boolean(
    process.env.NEXT_PUBLIC_SENTRY_DSN ||
      process.env.SENTRY_DSN ||
      process.env.REACT_APP_SENTRY_DSN,
  );

const hasDatadogRumCredentials = (): boolean =>
  Boolean(
    (process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID ||
      process.env.REACT_APP_DATADOG_APPLICATION_ID) &&
      (process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN || process.env.REACT_APP_DATADOG_CLIENT_TOKEN),
  );

/**
 * Resolves the active error monitoring provider.
 * 1. Explicit NEXT_PUBLIC_ / REACT_APP_ERROR_MONITORING_PROVIDER
 * 2. Backward compat: sentry if Sentry DSN set, else datadog if RUM creds set
 * 3. none
 */
export const getErrorMonitoringProvider = (): ErrorMonitoringProvider => {
  const explicit = getExplicitProvider();

  if (explicit) return explicit;

  if (hasSentryCredentials()) return "sentry";
  if (hasDatadogRumCredentials()) return "datadog";

  return "none";
};

export const isErrorMonitoringEnabled = (): boolean => getErrorMonitoringProvider() !== "none";

export const isSentryProvider = (): boolean => getErrorMonitoringProvider() === "sentry";

export const isDatadogProvider = (): boolean => getErrorMonitoringProvider() === "datadog";
